import { useState, useEffect } from 'react';
import { useSWRAlloy } from '@adsk/alloy-helpers';
import { fetchWithToken } from '@adsk/acs-sc-web-platform-api';
import { useLocale } from '@adsk/alloy-react-locale';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import { useFormikContext } from 'formik';
import { PERMISSION_TYPES } from '../constants';
import {
  buildGetRFIsPermissionsRequest,
  getRFIsPermissionsInfo,
} from './utils';

const useRFIsPermissions = () => {
  const [permissionsInfo, setPermissionsInfo] = useState({ loading: true });
  const { values } = useFormikContext();
  const {
    member: {
      autodeskId: memberAutodeskId,
      selectedCompany,
      selectedRoles,
      selectedAccessLevel,
    },
  } = values;
  const projectId = useCurrentProjectId();
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const { data: rfisPermissionsData, error: rfisPermissionsError } =
    useSWRAlloy(
      !projectId ? null : buildGetRFIsPermissionsRequest(projectId),
      fetchWithToken
    );

  useEffect(() => {
    if (!rfisPermissionsData) return;
    const { loading: permissionsLoading } = permissionsInfo;

    if (!permissionsLoading) return;

    const { projectRolesMapping, workflowType } = rfisPermissionsData;
    const memberPermissions = projectRolesMapping
      .filter((role) =>
        role.permittedAssignees.some(
          (assignee) =>
            (assignee.id === memberAutodeskId || assignee.id === 'all') &&
            assignee.type === PERMISSION_TYPES.USER
        )
      )
      .map((role) => role.name);
    const companyPermissions = projectRolesMapping
      .filter((role) =>
        role.permittedAssignees.some(
          (assignee) =>
            assignee.id === selectedCompany?.memberGroupId &&
            assignee.type === PERMISSION_TYPES.COMPANY
        )
      )
      .map((role) => role.name);
    const rolesPermissions = selectedRoles
      ? projectRolesMapping.reduce((result, role) => {
          const assignedRoles = selectedRoles.filter((roleInfo) =>
            role.permittedAssignees.some(
              (assignee) =>
                roleInfo.memberGroupId === assignee.id &&
                assignee.type === PERMISSION_TYPES.ROLE
            )
          );
          if (assignedRoles.length > 0)
            result.push({ name: role.name, roles: assignedRoles });

          return result;
        }, [])
      : [];
    const roles = projectRolesMapping.map((map) => map.name);

    const info = getRFIsPermissionsInfo(
      memberPermissions,
      companyPermissions,
      rolesPermissions,
      roles,
      workflowType,
      selectedAccessLevel?.value,
      selectedCompany,
      T
    );
    setPermissionsInfo(info);
  }, [
    memberAutodeskId,
    rfisPermissionsData,
    selectedAccessLevel,
    selectedCompany,
    selectedRoles,
    T,
    permissionsInfo,
  ]);

  useEffect(() => {
    if (!rfisPermissionsError) return;
    const { loading: permissionsLoading } = permissionsInfo;

    if (!permissionsLoading) return;

    setPermissionsInfo({});
  }, [permissionsInfo, rfisPermissionsError]);

  return permissionsInfo;
};

export default useRFIsPermissions;
